// Field-type metadata for the document template builder + signer. DocuSeal-style:
// a translucent color FILL at rest (no border), the field's-color BORDER only
// when selected, and a type ICON (not text) centered in the box. Color-coding is
// a builder affordance, so these use a small fixed palette.

import {
  Type,
  Text,
  Hash,
  Calendar,
  Pin,
  PenLine,
  CalendarClock,
  CheckSquare,
  CircleDot,
  Stamp,
  Table,
  RectangleEllipsis,
} from 'lucide-react';

// Palette grouped into regular fields vs signing fields.
export const FIELD_TYPE_GROUPS = [
  {
    title: 'Add field',
    options: [
      { type: 'text', label: 'Text' },
      { type: 'long_text', label: 'Long Text' },
      { type: 'static_text', label: 'Static Text' },
      { type: 'number', label: 'Number' },
      { type: 'digits', label: 'Digit boxes' },
      { type: 'date', label: 'Date' },
      { type: 'checkbox', label: 'Checkbox' },
      { type: 'radio', label: 'Radio' },
    ],
  },
  {
    title: 'Add signing field',
    options: [
      { type: 'signature', label: 'Signature' },
      { type: 'static_signature', label: 'Static Signature' },
      { type: 'signature_date', label: 'Sign Date' },
    ],
  },
  {
    // Prepare-time regions: rendered into the per-client prepared PDF at
    // SEND time (server-side), never filled by the signer. The builder only
    // reserves the space.
    title: 'Add prepared field',
    options: [{ type: 'fee_chart', label: 'Fee Chart' }],
  },
];

// Flat list (derived) for any consumer that just needs every option.
export const FIELD_TYPE_OPTIONS = FIELD_TYPE_GROUPS.flatMap((g) => g.options);

const META = {
  text: { label: 'Text', icon: Type, fill: 'bg-blue-400/15', border: 'border-blue-500', color: 'text-blue-600 dark:text-blue-300' },
  long_text: { label: 'Long Text', icon: Text, fill: 'bg-indigo-400/15', border: 'border-indigo-500', color: 'text-indigo-600 dark:text-indigo-300' },
  digits: { label: 'Digit boxes', icon: RectangleEllipsis, fill: 'bg-teal-400/15', border: 'border-teal-500', color: 'text-teal-600 dark:text-teal-300' },
  number: { label: 'Number', icon: Hash, fill: 'bg-cyan-400/15', border: 'border-cyan-500', color: 'text-cyan-600 dark:text-cyan-300' },
  date: { label: 'Date', icon: Calendar, fill: 'bg-violet-400/15', border: 'border-violet-500', color: 'text-violet-600 dark:text-violet-300' },
  static_text: { label: 'Static', icon: Pin, fill: 'bg-slate-400/15', border: 'border-slate-500', color: 'text-slate-600 dark:text-slate-300' },
  signature: { label: 'Signature', icon: PenLine, fill: 'bg-emerald-500/15', border: 'border-emerald-500', color: 'text-emerald-600 dark:text-emerald-300' },
  static_signature: { label: 'Static Signature', icon: Stamp, fill: 'bg-slate-400/15', border: 'border-slate-500', color: 'text-slate-600 dark:text-slate-300' },
  signature_date: { label: 'Sign Date', icon: CalendarClock, fill: 'bg-amber-400/15', border: 'border-amber-500', color: 'text-amber-600 dark:text-amber-300' },
  checkbox: { label: 'Checkbox', icon: CheckSquare, fill: 'bg-rose-400/15', border: 'border-rose-500', color: 'text-rose-600 dark:text-rose-300' },
  radio: { label: 'Radio', icon: CircleDot, fill: 'bg-fuchsia-400/15', border: 'border-fuchsia-500', color: 'text-fuchsia-600 dark:text-fuchsia-300' },
  fee_chart: { label: 'Fee Chart', icon: Table, fill: 'bg-orange-400/15', border: 'border-orange-500', color: 'text-orange-600 dark:text-orange-300' },
};

export const fieldTypeMeta = (type) => META[type] || META.text;

// Types that render text (so font size + alignment apply).
export const TEXTUAL_TYPES = new Set([
  'text',
  'long_text',
  'number',
  'date',
  'static_text',
  'signature_date',
  'digits',
]);

// Auto-filled types the signer never inputs. fee_chart is prepare-time ink.
export const AUTO_TYPES = new Set([
  'static_text',
  'signature_date',
  'static_signature',
  'fee_chart',
]);

// A field the signer never types: an auto type, or a Date set to "Today's
// date" (Nic 2026-10-21). Mirrors the server's firstMissingRequired skip.
export const isAutoField = (p) => !!p && (AUTO_TYPES.has(p.type) || (p.type === 'date' && !!p.autoToday));

// Today in the business's timezone (Central), MM/DD/YYYY: what the server
// stamps for a Sign Date or a "Today's date" (documentPdf formatSignDate).
// Was UTC until 2026-10-21, which showed tomorrow after 7 PM Central.
export const todayInCentral = (now = new Date()) => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .formatToParts(now)
      .map((x) => [x.type, x.value]),
  );
  return `${parts.month}/${parts.day}/${parts.year}`;
};

// Prepare-time region types (mirrors server PREPARE_ONLY_FIELD_TYPES): the
// send flow renders these into the prepared per-client PDF; the signer never
// sees them as fields.
export const PREPARE_ONLY_TYPES = new Set(['fee_chart']);

// Default field box size (normalized fraction of the page) by type. The
// checkbox w/h are picked so it renders ~visually square on a US-letter page
// (page is taller than wide, so w_norm > h_norm for equal pixels).
export const defaultSizeFor = (type) => {
  if (type === 'signature' || type === 'static_signature') return { w: 0.25, h: 0.06 };
  if (type === 'long_text') return { w: 0.32, h: 0.12 };
  if (type === 'checkbox' || type === 'radio') return { w: 0.026, h: 0.02 };
  // The 12-column fee table wants most of the page width.
  if (type === 'fee_chart') return { w: 0.85, h: 0.1 };
  if (type === 'digits') return { w: 0.25, h: 0.025 };
  return { w: 0.25, h: 0.03 };
};

// Line-height multiple used for multi-line (long_text) layout — shared by the
// builder's live line-count estimate and the burn engine so they agree.
export const LINE_HEIGHT_FACTOR = 1.2;

// How many text lines a long_text box fits, given its normalized height, the
// page height in PDF points, and the field's font size (points).
export const linesForBox = (heightFrac, pageHeightPts, fontSize) => {
  if (!pageHeightPts) return 1;
  const lineHeight = (fontSize || 11) * LINE_HEIGHT_FACTOR;
  return Math.max(1, Math.floor((heightFrac * pageHeightPts) / lineHeight));
};

export const newFieldId = () =>
  `f-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// Types that render a real, focusable text input (so auto-advance can land on them).
const FOCUSABLE_INPUT_TYPES = new Set(['text', 'long_text', 'number', 'date']);

// A group's members in authored order: by `groupOrder` when set, else document
// (array) order. Shared by the builder's reorderable list and the signer.
export const groupMembersOrdered = (placements, gid) =>
  (placements || [])
    .map((p, i) => ({ p, i }))
    .filter((x) => x.p.group === gid)
    .sort((a, b) => {
      const ao = a.p.groupOrder;
      const bo = b.p.groupOrder;
      if (ao != null && bo != null) return ao - bo;
      if (ao != null) return -1;
      if (bo != null) return 1;
      return a.i - b.i;
    })
    .map((x) => x.p);

// Where focus should jump after `currentId` fills up (auto-advance): the next
// focusable member in its group by order. Auto-advance is group-scoped, so it
// STOPS at the end of the group (never leaks into the next group). Returns a
// fieldId or null. Targets only real text inputs.
export const nextFocusableFieldId = (placements, currentId) => {
  const cur = (placements || []).find((p) => p.fieldId === currentId);
  if (!cur) return null;
  const focusable = (p) => p && FOCUSABLE_INPUT_TYPES.has(p.type);
  if (cur.group) {
    const members = groupMembersOrdered(placements, cur.group);
    const idx = members.findIndex((p) => p.fieldId === currentId);
    for (let i = idx + 1; i < members.length; i += 1) {
      if (focusable(members[i])) return members[i].fieldId;
    }
    return null; // last focusable in the group → stop here
  }
  // Ungrouped: next focusable field in document order.
  const docIdx = placements.findIndex((p) => p.fieldId === currentId);
  for (let i = docIdx + 1; i < placements.length; i += 1) {
    if (focusable(placements[i])) return placements[i].fieldId;
  }
  return null;
};

// Whether a conditional rule { fieldId, op } is satisfied. Mirrors the burn
// engine's ruleMet (server/lib/documentPdf.js).
export const ruleMet = (rule, values) => {
  if (!rule || !rule.fieldId) return false;
  const v = values ? values[rule.fieldId] : undefined;
  if (rule.op === 'filled') return v !== undefined && v !== null && String(v).trim() !== '';
  if (rule.op === 'equals') {
    return String(v == null ? '' : v) === String(rule.value == null ? '' : rule.value);
  }
  return Boolean(v); // 'checked'
};

// A field with an `enabledIf` rule is active only when met; otherwise greyed +
// locked (and not required).
export const fieldEnabled = (p, values) => (p && p.enabledIf ? ruleMet(p.enabledIf, values) : true);

// A field is required if always required, or its `requiredIf` rule is met.
export const fieldRequired = (p, values) =>
  Boolean(p && p.required) || (p && p.requiredIf ? ruleMet(p.requiredIf, values) : false);

// What the builder's red asterisk says about a field (Nic 2026-10-15):
// 'always' (required, or a signature, which the signer must always do),
// 'conditional' (a "required when" rule) or 'oneOf' (a group where one is
// required). null = optional.
export const requiredMark = (p) => {
  if (!p) return null;
  if (p.type === 'signature' || p.required) return 'always';
  if (p.requiredIf) return 'conditional';
  if (p.group && p.groupRequired) return 'oneOf';
  return null;
};

export const parsePlacements = (raw) => {
  if (Array.isArray(raw)) return raw;
  try {
    return JSON.parse(raw || '[]');
  } catch (e) {
    return [];
  }
};

// ---- Digit boxes (Nic 2026-10-15) -------------------------------------------
// ONE field drawn as N one-character boxes (an SSN in the W-9's 3-2-4 boxes).
// Mirrors server/lib/digitBoxes.js exactly; keep the two identical.
export const DIGIT_CELLS_MIN = 2;
export const DIGIT_CELLS_MAX = 20;
export const DIGIT_GAP_MAX = 3;
// Common layouts for the builder's preset picker.
export const DIGIT_PRESETS = [
  // SSN and EIN switch Sensitive on (QA 2026-10-15): a tax number is
  // masked on phones and never kept raw on the envelope row.
  { key: 'ssn', label: 'SSN (3-2-4)', cells: 9, segments: [3, 2, 4], sensitive: true },
  { key: 'ein', label: 'EIN (2-7)', cells: 9, segments: [2, 7], sensitive: true },
  { key: 'phone', label: 'Phone (3-3-4)', cells: 10, segments: [3, 3, 4] },
  { key: 'zip', label: 'ZIP (5)', cells: 5, segments: [5] },
];

export const digitCellsOf = (p) => {
  const n = parseInt(p && p.cells, 10);
  if (!Number.isFinite(n)) return 9;
  return Math.max(DIGIT_CELLS_MIN, Math.min(DIGIT_CELLS_MAX, n));
};
export const digitSegmentsOf = (p, cells = digitCellsOf(p)) => {
  const raw = Array.isArray(p && p.segments) ? p.segments : null;
  if (!raw || !raw.length) return [cells];
  const segs = raw.map((s) => parseInt(s, 10));
  if (segs.some((s) => !Number.isFinite(s) || s < 1)) return [cells];
  return segs.reduce((a, b) => a + b, 0) === cells ? segs : [cells];
};
const digitGapOf = (p) => {
  const g = Number(p && p.cellGap);
  return Number.isFinite(g) ? Math.max(0, Math.min(DIGIT_GAP_MAX, g)) : 0;
};
// The boxes as fractions of the field's width: [{ left, width }].
export const digitBoxLayout = (p) => {
  const cells = digitCellsOf(p);
  const segments = digitSegmentsOf(p, cells);
  const gap = segments.length > 1 ? digitGapOf(p) : 0;
  const unit = 1 / (cells + gap * (segments.length - 1));
  const boxes = [];
  let at = 0;
  segments.forEach((n, si) => {
    for (let i = 0; i < n; i += 1) {
      boxes.push({ left: at * unit, width: unit });
      at += 1;
    }
    if (si < segments.length - 1) at += gap;
  });
  return { cells, segments, boxes };
};
export const cleanDigitsValue = (p, raw) => {
  const s = String(raw == null ? '' : raw);
  const kept = p && p.allowLetters ? s.replace(/[^0-9A-Za-z]/g, '') : s.replace(/\D/g, '');
  return kept.slice(0, digitCellsOf(p));
};
export const digitsComplete = (p, raw) => cleanDigitsValue(p, raw).length === digitCellsOf(p);
// Shown with a dash between runs: 123-45-6789.
export const formatDigits = (p, raw) => {
  const v = cleanDigitsValue(p, raw);
  const out = [];
  let at = 0;
  digitSegmentsOf(p).forEach((n) => {
    if (at < v.length) out.push(v.slice(at, at + n));
    at += n;
  });
  return out.join('-');
};
